<script setup>
import { ref, computed } from 'vue'
import { router, Link, usePage } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import Avatar from 'primevue/avatar'
import Menu from 'primevue/menu'
import { Badge } from 'primevue'
import Popover from 'primevue/popover'
import Button from 'primevue/button'
import NotificationClickDialog from '../notifications/NotificationClickDialog.vue'
import { getInitials, useFormatDate } from '../../../../../helpers.js'

const { props } = usePage()

const menu                = ref(null)
const notificationsPopover = ref(null)
const currentNotification = ref(null)
const readNotificationIds = ref([])
const currentPage         = ref(1)
const PAGE_SIZE           = 8

const user         = computed(() => props.user)
const userInitials = computed(() => props.user_initials)

const notifications         = computed(() => props.notifications || [])
const displayedNotifications = computed(() =>
    notifications.value
        .filter(({ id }) => !readNotificationIds.value.includes(id))
        .slice(0, currentPage.value * PAGE_SIZE)
)
const unreadCount     = computed(() => displayedNotifications.value.length)
const seenCount       = ref(0)
const hasNotifications = computed(() => displayedNotifications.value.length > 0)

const toggleMenu          = (e) => menu.value.toggle(e)
const toggleNotifications = (e) => {
    notificationsPopover.value.toggle(e)
    seenCount.value = displayedNotifications.value.length
}

const showDialog = (n) => { currentNotification.value = n }
const loadMore   = () => { currentPage.value++ }

const { formatDate } = useFormatDate()

const markAsRead = async (id) => {
    try {
        await axios.put(`/notification_read/${id}`, {})
        if (!readNotificationIds.value.includes(id)) readNotificationIds.value.push(id)
    } catch (e) {
        console.error(e)
    }
}

const markAllAsRead = async () => {
    try {
        await axios.put('/notification_all_read', {})
        readNotificationIds.value = notifications.value.map(({ id }) => id)
    } catch (e) {
        console.error(e)
    }
}

const iconForNotification = (n) => {
    if (n.data.type === 'new_reservation') return 'pi-plus'
    if (n.data.change_request?.action === 'cancel' || n.data.type === 'canceled_reservation') return 'pi-trash'
    return 'pi-arrow-right-arrow-left'
}

const accountItems = computed(() => [
    { label: trans('common.nav_user_settings'), icon: 'pi pi-fw pi-user', command: () => router.get('/app/settings') },
    { label: trans('common.nav_logout'),        icon: 'pi pi-sign-out',   command: () => router.get('/logout') },
])
</script>

<template>
    <div class="flex items-center gap-1 pointer-events-auto">
        <!-- Nastavení -->
        <Link href="/app/settings">
            <Button icon="pi pi-cog" variant="text" size="large" severity="secondary" rounded class="max-md:!hidden"/>
        </Link>

        <!-- Notifikace -->
        <div class="relative" @click="toggleNotifications">
            <Badge
                v-if="unreadCount"
                :value="unreadCount"
                size="small"
                :severity="unreadCount !== seenCount ? 'danger' : 'contrast'"
                class="absolute z-50 right-0 cursor-pointer"
            />
            <Button icon="pi pi-bell" variant="text" size="large" severity="secondary" rounded/>
        </div>

        <!-- Avatar -->
        <Avatar
            v-if="user"
            :label="userInitials"
            shape="circle"
            class="ml-1 cursor-pointer"
            size="normal"
            @click="toggleMenu"
        />
        <Menu ref="menu" :model="accountItems" id="overlay_topbar_menu" popup/>

        <!-- Notifikace popover -->
        <Popover ref="notificationsPopover" class="!rounded-2xl">
            <div class="p-1 w-80">
                <div class="flex justify-between items-center mb-2">
                    <h3 class="text-lg font-semibold">{{ $t('common.notifications') }}</h3>
                    <Button v-if="hasNotifications" :label="$t('common.mark_all_read')" @click="markAllAsRead" size="small" severity="secondary"/>
                </div>
                <template v-if="hasNotifications">
                    <div class="flex flex-col gap-2">
                        <div
                            v-for="n in displayedNotifications" :key="n.id"
                            class="bg-white dark:bg-neutral-800 dark:border-neutral-700 shadow rounded-lg p-3 flex gap-3 cursor-pointer hover:shadow-md transition-all border border-gray-200 items-center relative mouseoverable"
                            :class="{ 'opacity-50': n.read_at }"
                            @click="showDialog(n)"
                        >
                            <div class="relative mt-0.5">
                                <Avatar :label="getInitials(n.data.sender)"/>
                                <p class="absolute -bottom-1 -right-1 bg-blue-500 text-white h-5 flex justify-center items-center border-2 border-white dark:border-neutral-800 w-5 rounded-full">
                                    <i class="pi !text-[.5rem]" :class="iconForNotification(n)"/>
                                </p>
                            </div>
                            <div class="text-xs grow overflow-hidden">
                                <p class="text-sm break-words">{{ n.data.message }}</p>
                                <span class="opacity-50">{{ formatDate(n.created_at) }}</span>
                            </div>
                            <div class="flex gap-3 justify-between show-mouseover">
                                <i class="pi pi-trash text-blue-600 cursor-pointer" @click.stop="markAsRead(n.id)"/>
                            </div>
                        </div>
                    </div>
                    <div v-if="notifications.length > PAGE_SIZE" class="mt-3 text-center">
                        <button @click="loadMore" class="text-roori-blue font-semibold text-xs hover:underline">{{ $t('common.load_more') }}</button>
                    </div>
                </template>
                <template v-else>
                    <p class="text-gray-500 text-sm">{{ $t('common.no_notifications') }}</p>
                </template>
            </div>
        </Popover>

        <NotificationClickDialog v-model="currentNotification" @read="markAsRead(currentNotification?.id)"/>
    </div>
</template>
